/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { ModalAwareScrollView } from '../modals/ModalAwareScrollView';
import {
  RefreshControl,
  StyleSheet,
  View,
  type ScrollViewProps,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { type KeyboardAwareScrollViewRef } from 'react-native-keyboard-controller';
import { colors, spacing, webOperationalScrollBindings } from '../../shared/theme/public-api';
import type { ScreenLayoutVariant } from './breakpoints';
import { useContentLayout } from './useContentLayout';

type Props = Omit<ScrollViewProps, 'contentContainerStyle'> & {
  children: React.ReactNode;
  contentContainerStyle?: StyleProp<ViewStyle>;
  /** Max-width variant; defaults to settings (880). Use settingsWide (960) on web shell. */
  widthVariant?: ScreenLayoutVariant;
  /** Center column with max width on wide viewports. */
  constrainWidth?: boolean;
  /** Padding extra bajo el contenido cuando el teclado está abierto. */
  keyboardBottomPadding?: number;
  refreshing?: boolean;
  onRefresh?: () => void;
};

/**
 * Scroll container for shell stack screens (Profile, BranchSettings, CashManagement).
 * Centers content and caps width on PC.
 */
export const ShellScrollScreen = React.forwardRef<KeyboardAwareScrollViewRef, Props>(function ShellScrollScreen(
  {
    children,
    style,
    contentContainerStyle,
    widthVariant = 'settings',
    constrainWidth = true,
    keyboardBottomPadding,
    refreshing,
    onRefresh,
    ...scrollProps
  },
  ref,
) {
  const { contentMaxWidth, layoutWide } = useContentLayout(widthVariant);

  const refreshControl =
    onRefresh != null ? (
      <RefreshControl
        refreshing={refreshing ?? false}
        onRefresh={onRefresh}
        colors={[colors.textSecondary]}
        tintColor={colors.textSecondary}
      />
    ) : undefined;

  return (
    <ModalAwareScrollView
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={ref as any}
      {...webOperationalScrollBindings([styles.scroll, style])}
      contentContainerStyle={[styles.contentOuter, contentContainerStyle]}
      keyboardShouldPersistTaps="handled"
      bottomOffset={keyboardBottomPadding}
      refreshControl={refreshControl}
      {...scrollProps}
    >
      <View
        style={[
          styles.inner,
          constrainWidth && contentMaxWidth != null ? { maxWidth: contentMaxWidth, width: '100%' } : null,
          layoutWide ? styles.innerWide : null,
        ]}
      >
        {children}
      </View>
    </ModalAwareScrollView>
  );
});

/** Two-column row for settings sections on wide layout. */
export function ShellTwoColumnRow({
  children,
  layoutWide,
  style,
}: {
  children: React.ReactNode;
  layoutWide: boolean;
  style?: StyleProp<ViewStyle>;
}) {
  if (!layoutWide) {
    return <View style={style}>{children}</View>;
  }
  return <View style={[styles.twoCol, style]}>{children}</View>;
}

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
    backgroundColor: colors.background,
  },
  contentOuter: {
    flexGrow: 1,
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.xxl,
  },
  inner: {
    alignSelf: 'center',
    paddingTop: spacing.md,
    gap: spacing.md,
  },
  innerWide: {
    paddingTop: spacing.lg,
  },
  twoCol: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
    alignItems: 'stretch',
    alignSelf: 'stretch',
    width: '100%',
  },
});
